import { useParams } from 'react-router-dom';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Text } from '@newcore/ui/typography';
import { ROUTES } from '@/common/auth-routes';
import { buildTenantPath } from '@/common/tenant-utils';
import { validateOnboardingToken } from '@/features/onboarding/api/onboarding-api';
import { InstallStep } from '@/features/onboarding/components/install-step';
import { MobileInstallApp } from '@/features/onboarding/components/mobile-install-app';
import { OnboardingBackground } from '@/features/onboarding/components/onboarding-background';
import { OnboardingTokenValidationStatusScreen } from '@/features/onboarding/components/onboarding-token-validation-status-screen';
import { OnboardingWizardContainer } from '@/features/onboarding/components/onboarding-wizard-container';
import { Onboarding } from '@/features/onboarding/components/primitives';
import { UserOnboardingWizard } from '@/features/onboarding/components/wizards';
import {
  useTokenValidation,
  ValidationStatus,
} from '@/features/onboarding/hooks/use-token-validation';
import { isMobileDevice } from '@/features/onboarding/utils/device-detection';
import { createOnboardingDeepLinkUrl } from '@/features/onboarding/utils/mobile-platform';

const INVITE_INSTALL_TITLE = 'Install NewCore to activate your account';

export function OnboardingPage() {
  const { token } = useParams<{ token: string }>();

  const { status, validationResult, error } = useTokenValidation({
    token,
    validateToken: validateOnboardingToken,
  });

  const handleComplete = () => {
    const tenantSlug = validationResult?.tenant_slug;
    window.location.href = tenantSlug
      ? buildTenantPath(tenantSlug, ROUTES.DASHBOARD)
      : ROUTES.TENANT_SELECTION;
  };

  if (status !== ValidationStatus.VALID) {
    return (
      <OnboardingTokenValidationStatusScreen
        status={status}
        validationResult={validationResult}
        error={error}
        buildSignInUrl={tenantSlug =>
          buildTenantPath(tenantSlug, ROUTES.DASHBOARD)
        }
      />
    );
  }

  const isMobileOnboardingEligible =
    validationResult?.is_mobile_onboarding_eligible ?? false;

  if (isMobileOnboardingEligible && token && isMobileDevice()) {
    return (
      <MobileInstallApp
        deepLinkUrl={createOnboardingDeepLinkUrl(token)}
        title={INVITE_INSTALL_TITLE}
      >
        <InstallStep number={2}>Go back to the invitation email</InstallStep>
        <InstallStep number={3}>
          Tap{' '}
          <Text as='strong' size={TextSizes.MD} weight={FontWeights.SEMIBOLD}>
            Activate your account
          </Text>{' '}
          again, then{' '}
          <Text as='strong' size={TextSizes.MD} weight={FontWeights.SEMIBOLD}>
            Open NewCore
          </Text>
        </InstallStep>
      </MobileInstallApp>
    );
  }

  if (!token || !validationResult) {
    return null;
  }

  return (
    <OnboardingWizardContainer>
      <OnboardingBackground />
      <Onboarding.Dialog open={true} dismissible={false} size='md'>
        <UserOnboardingWizard
          token={token}
          userName={
            validationResult.user_first_name ??
            validationResult.user_display_name
          }
          isMobileOnboardingEligible={isMobileOnboardingEligible}
          tenantSlug={validationResult.tenant_slug}
          onComplete={handleComplete}
        />
      </Onboarding.Dialog>
    </OnboardingWizardContainer>
  );
}

export default OnboardingPage;
